import { createSlice } from "@reduxjs/toolkit";

import { EMPTY_ARRAY } from "Utils";

import {
  fetchGetStationVehiclesFromCurrentSemanticMap,
  fetchGetMissionsInQueueFromCurrentSemanticMap,
  fetchGetStationVehiclesFromCurrentSemanticMapAndStationId,
} from "./actions";
import { DataStatus } from "../../types";

type StationConfirmationState = {
  data: StationVehicle[];
  missionsQueue: QueuedMission[];
  loadedSemanticMapId: string;
  stationId: string;
  status: DataStatus;
  missionQueueStatus: DataStatus;
  error: string | null;
};

const initialState: StationConfirmationState = {
  data: [],
  missionsQueue: [],
  loadedSemanticMapId: "",
  stationId: "",
  status: DataStatus.idle,
  missionQueueStatus: DataStatus.idle,
  error: null,
};

function updateVehicleProperties(
  vehicle: StationVehicle,
  updatedVehicle: StationVehicle
) {
  const {
    cloudConfirmations,
    version,
    hmiButtonConfirmed,
    completed,
    loop,
    updatedAt,
    missionName,
  } = updatedVehicle;
  vehicle.cloudConfirmations = [...cloudConfirmations];
  // get the latest version number
  vehicle.version = version;
  // update the hmiButtonConfirmed to show the latest status
  vehicle.hmiButtonConfirmed = hmiButtonConfirmed;
  // if completed is true, the vehicle is hidden in the UI
  vehicle.completed = completed;
  vehicle.loop = loop;
  // update the updatedAt which is used to sort the vehicles
  vehicle.updatedAt = updatedAt;
  // update the mission name if provided
  if (missionName) {
    vehicle.missionName = missionName;
  }
}

export const Slice = createSlice({
  name: "stationConfirmation",
  initialState,
  reducers: {
    resetStations: () => initialState,
    setInitStationConfirmation: (state, { payload }) => {
      state.data = payload?.sessions ?? EMPTY_ARRAY;
      state.loadedSemanticMapId = payload?.semantic_map_id ?? "";
    },
    updateStationConfirmation: (state, { payload }) => {
      const vehicle = state.data.find((vehicle) => vehicle.id === payload.id);
      // Update the cloudConfirmations property
      if (vehicle) {
        updateVehicleProperties(vehicle, payload);
      } else {
        state.data.push(payload);
      }
      return state;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchGetStationVehiclesFromCurrentSemanticMap.pending,
        (state) => {
          state.status = DataStatus.loading;
          state.error = null;
        }
      )
      .addCase(
        fetchGetStationVehiclesFromCurrentSemanticMap.fulfilled,
        (state, action) => {
          state.status = DataStatus.succeeded;
          // state.data = state.data.concat(action.payload.stations);
          state.data = action.payload.stations;
          state.loadedSemanticMapId = action.payload.semanticMapId;
        }
      )
      .addCase(
        fetchGetStationVehiclesFromCurrentSemanticMap.rejected,
        (state, action) => {
          state.status = DataStatus.failed;
          state.error = action.error.message ?? "";
        }
      )
      .addCase(
        fetchGetMissionsInQueueFromCurrentSemanticMap.pending,
        (state) => {
          state.missionQueueStatus = DataStatus.loading;
          state.error = null;
        }
      )
      .addCase(
        fetchGetMissionsInQueueFromCurrentSemanticMap.fulfilled,
        (state, action) => {
          state.missionQueueStatus = DataStatus.succeeded;
          state.missionsQueue = action.payload.missionsQueue;
          state.loadedSemanticMapId = action.payload.semanticMapId;
        }
      )
      .addCase(
        fetchGetMissionsInQueueFromCurrentSemanticMap.rejected,
        (state, action) => {
          state.missionQueueStatus = DataStatus.failed;
          state.error = action.error.message ?? "";
        }
      )
      .addCase(
        fetchGetStationVehiclesFromCurrentSemanticMapAndStationId.pending,
        (state) => {
          state.status = DataStatus.loading;
          state.error = null;
        }
      )
      .addCase(
        fetchGetStationVehiclesFromCurrentSemanticMapAndStationId.fulfilled,
        (state, action) => {
          state.status = DataStatus.succeeded;
          state.data = action.payload.stations;
          state.stationId = action.payload.stationId;
        }
      )
      .addCase(
        fetchGetStationVehiclesFromCurrentSemanticMapAndStationId.rejected,
        (state, action) => {
          state.status = DataStatus.failed;
          state.error = action.error.message ?? "";
        }
      );
  },
});

export default Slice;
